import { atom } from 'jotai';
import { signIn, signOut, getCurrentUser, completeNewPassword, logoutFromHostedUI, type UserInfo } from '../services/cognitoAuth';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';

// Authentication state
export interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  email: string | null;
  userId: string | null;  // Cognito sub
  groups: string[];
  isAdmin: boolean;
  accessToken: string | null;
  idToken: string | null;
}

// Helper to get auth state from localStorage
const getStoredAuthState = (): AuthState => {
  try {
    const stored = localStorage.getItem('talrev_auth');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to parse stored auth state:', error);
  }
  return {
    isAuthenticated: false,
    username: null,
    email: null,
    userId: null,
    groups: [],
    isAdmin: false,
    accessToken: null,
    idToken: null,
  };
};

// Helper to save auth state to localStorage
const saveAuthState = (state: AuthState) => {
  try {
    localStorage.setItem('talrev_auth', JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save auth state:', error);
  }
};

// Initialize auth state from localStorage or check tokens
const initializeAuthState = (): AuthState => {
  try {
    // IMPORTANT: Check if we just returned from Cognito logout
    // Cognito logout redirects to app root, so if we have no URL params and no stored auth,
    // we should ensure everything is clean
    const hasUrlParams = window.location.search.includes('code=') || window.location.search.includes('error=');

    // Check if we have tokens in localStorage (OAuth flow)
    const accessToken = localStorage.getItem('access_token');
    const idToken = localStorage.getItem('id_token');
    const storedAuth = localStorage.getItem('talrev_auth');

    console.log('🔐 Initializing auth state:', {
      hasUrlParams,
      hasAccessToken: !!accessToken,
      hasIdToken: !!idToken,
      hasStoredAuth: !!storedAuth,
      pathname: window.location.pathname
    });

    // If coming back from logout (no params, at root, no stored auth), ensure clean state
    if (!hasUrlParams && window.location.pathname === '/' && !storedAuth) {
      console.log('🧹 Detected post-logout state, clearing any stale tokens');
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('refresh_token');
      return {
        isAuthenticated: false,
        username: null,
        email: null,
        userId: null,
        groups: [],
        isAdmin: false,
        accessToken: null,
        idToken: null,
      };
    }

    // If we have tokens, we're authenticated
    if (accessToken && idToken) {
      // Try to get stored auth state
      if (storedAuth) {
        try {
          const parsedAuth = JSON.parse(storedAuth);
          // Make sure isAuthenticated is true
          return {
            ...parsedAuth,
            isAuthenticated: true,
          };
        } catch (error) {
          console.error('Failed to parse stored auth state:', error);
        }
      }

      // If we have tokens but no stored auth state, decode the ID token
      try {
        const payload = jwtDecode<any>(idToken);
        const authState: AuthState = {
          isAuthenticated: true,
          username: payload.email,
          email: payload.email,
          userId: payload.sub,
          groups: payload['cognito:groups'] || [],
          isAdmin: (payload['cognito:groups'] || []).includes('admin'),
          accessToken: accessToken,
          idToken: idToken,
        };
        saveAuthState(authState);
        return authState;
      } catch (error) {
        console.error('Failed to decode ID token:', error);
      }
    }
  } catch (error) {
    console.error('Failed to initialize auth state:', error);
  }

  // No valid session, return unauthenticated state
  return {
    isAuthenticated: false,
    username: null,
    email: null,
    userId: null,
    groups: [],
    isAdmin: false,
    accessToken: null,
    idToken: null,
  };
};

// Initialize auth state from localStorage/tokens
const initialAuthState: AuthState = initializeAuthState();

// Auth atom - initialized from localStorage
export const authAtom = atom<AuthState>(initialAuthState);

// Login action atom with Cognito integration
export const loginAtom = atom(
  null,
  async (get, set, { email, password }: { email: string; password: string }) => {
    try {
      // Attempt to sign in with Cognito
      const result = await signIn(email, password);

      const newAuthState: AuthState = {
        isAuthenticated: true,
        username: result.user.username,
        email: result.user.email,
        userId: result.user.userId,
        groups: result.user.groups,
        isAdmin: result.user.isAdmin,
        accessToken: result.accessToken,
        idToken: result.idToken,
      };

      set(authAtom, newAuthState);
      saveAuthState(newAuthState);

      return { success: true, user: result.user };
    } catch (error: any) {
      console.error('Login failed:', error);

      // Handle new password required
      if (error.code === 'NewPasswordRequired') {
        return {
          success: false,
          needsPasswordChange: true,
          error: error.message,
          cognitoUser: error.cognitoUser,
          userAttributes: error.userAttributes,
        };
      }

      return {
        success: false,
        error: error.message || 'Authentication failed. Please check your credentials.'
      };
    }
  }
);

// Complete new password challenge atom
export const completeNewPasswordAtom = atom(
  null,
  async (
    get,
    set,
    {
      cognitoUser,
      newPassword,
      userAttributes,
    }: {
      cognitoUser: CognitoUser;
      newPassword: string;
      userAttributes: any;
    }
  ) => {
    try {
      const result = await completeNewPassword(cognitoUser, newPassword, userAttributes);

      const newAuthState: AuthState = {
        isAuthenticated: true,
        username: result.user.username,
        email: result.user.email,
        userId: result.user.userId,
        groups: result.user.groups,
        isAdmin: result.user.isAdmin,
        accessToken: result.accessToken,
        idToken: result.idToken,
      };

      set(authAtom, newAuthState);
      saveAuthState(newAuthState);

      return { success: true, user: result.user };
    } catch (error: any) {
      console.error('Password change failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to change password'
      };
    }
  }
);

// Logout action atom with Cognito Hosted UI logout
export const logoutAtom = atom(
  null,
  async (get, set) => {
    console.log('🚪 Starting logout process...');

    // IMPORTANT: Clear localStorage FIRST before any state updates
    // This prevents race conditions where the redirect happens before cleanup
    localStorage.removeItem('talrev_auth');
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Clear auth state
    const loggedOutState: AuthState = {
      isAuthenticated: false,
      username: null,
      email: null,
      userId: null,
      groups: [],
      isAdmin: false,
      accessToken: null,
      idToken: null,
    };

    set(authAtom, loggedOutState);

    console.log('✅ Auth state cleared');

    // Only redirect if not already on logout page
    // This prevents unnecessary navigation that can cause race conditions
    if (window.location.pathname !== '/logout') {
      console.log('Redirecting to /logout page...');
      window.location.href = '/logout';
    } else {
      console.log('Already on /logout page, no redirect needed');
    }
  }
);

// Derived atom to check if user is authenticated
export const isAuthenticatedAtom = atom((get) => get(authAtom).isAuthenticated);

// Derived atom to get current username
export const currentUserAtom = atom((get) => get(authAtom).username);

// Derived atom to check if user is admin
export const isAdminAtom = atom((get) => get(authAtom).isAdmin);

// Derived atom to get user email
export const userEmailAtom = atom((get) => get(authAtom).email);

// Derived atom to get access token
export const accessTokenAtom = atom((get) => get(authAtom).accessToken);